/**
 * Dependency extraction for expressions and DSL instructions
 * Used for reactive form systems to track field changes
 */

import { dependencies as engineDependencies } from '@crown/dsl-compiler';

import { type ExprNode, type MatchPattern } from './expression.js';
import type { DslArgumentTokenType, DslInstruction } from './parser.js';

/**
 * Extract field dependencies by walking the AST
 * Avoids duplicate parsing - reuses the compiled AST
 */
function extractPatternDeps(
  pattern: MatchPattern,
  deps: Set<string>,
  locals: ReadonlySet<string>,
): void {
  switch (pattern.kind) {
    case 'wildcard':
    case 'binding_pat':
      return;
    case 'literal_pat':
      extractDepsFromAst(pattern.expr, deps, locals);
      return;
    case 'range_pat':
      extractDepsFromAst(pattern.from, deps, locals);
      extractDepsFromAst(pattern.to, deps, locals);
      return;
    case 'or_pat':
      for (const child of pattern.patterns)
        extractPatternDeps(child, deps, locals);
  }
}

function patternBindings(
  pattern: MatchPattern,
  out = new Set<string>(),
): Set<string> {
  if (pattern.kind === 'binding_pat') out.add(pattern.name);
  if (pattern.kind === 'or_pat')
    for (const child of pattern.patterns) patternBindings(child, out);
  return out;
}

function extractDepsFromAst(
  node: ExprNode,
  deps: Set<string>,
  locals: ReadonlySet<string> = new Set(),
): void {
  switch (node.kind) {
    case 'identifier': {
      const [rootName, ...rest] = node.chain;

      if (rootName === 'root' && rest.length > 0 && rest[0]) {
        deps.add(rest[0]);
      } else if (
        rootName &&
        rootName !== 'value' &&
        rootName !== 'root' &&
        rootName !== '$' &&
        !locals.has(rootName)
      ) {
        deps.add(rootName);
      }
      break;
    }
    case 'unary':
    case 'typeof':
      extractDepsFromAst(node.arg, deps, locals);
      break;
    case 'binary':
      extractDepsFromAst(node.left, deps, locals);
      extractDepsFromAst(node.right, deps, locals);
      break;
    case 'ternary':
      extractDepsFromAst(node.cond, deps, locals);
      extractDepsFromAst(node.then, deps, locals);
      extractDepsFromAst(node.else, deps, locals);
      break;
    case 'call':
      for (const arg of node.args) {
        extractDepsFromAst(arg, deps, locals);
      }
      break;
    case 'sequence': {
      const sequenceLocals = new Set(locals);
      for (const statement of node.statements) {
        extractDepsFromAst(statement, deps, sequenceLocals);
        if (statement.kind === 'assignment') sequenceLocals.add(statement.name);
      }
      break;
    }
    case 'assignment':
      extractDepsFromAst(node.value, deps, locals);
      break;
    case 'array':
      for (const element of node.elements)
        extractDepsFromAst(element, deps, locals);
      break;
    case 'object':
      for (const property of node.properties)
        extractDepsFromAst(property.value, deps, locals);
      break;
    case 'pipeline':
      if (node.stages[0]) extractDepsFromAst(node.stages[0], deps, locals);
      for (const stage of node.stages.slice(1)) {
        // A bare pipeline stage is a function name, not a root field.
        if (stage.kind === 'identifier' && stage.chain.length === 1) continue;
        extractDepsFromAst(stage, deps, new Set([...locals, '$']));
      }
      break;
    case 'match':
      extractDepsFromAst(node.expr, deps, locals);
      for (const arm of node.cases) {
        extractPatternDeps(arm.pattern, deps, locals);
        const armLocals = new Set([...locals, ...patternBindings(arm.pattern)]);
        if (arm.guard) extractDepsFromAst(arm.guard, deps, armLocals);
        extractDepsFromAst(arm.body, deps, armLocals);
      }
      break;
    case 'arrow':
      extractDepsFromAst(
        node.body,
        deps,
        new Set([...locals, ...node.params.map((param) => param.name)]),
      );
      break;
    case 'foreach':
      extractDepsFromAst(node.source, deps, locals);
      extractDepsFromAst(node.body, deps, new Set([...locals, node.binding]));
      break;
    case 'template':
      for (const part of node.parts)
        if (typeof part !== 'string') extractDepsFromAst(part, deps, locals);
      break;
    case 'literal':
    case 'debugger':
      break;
  }
}

/**
 * Which fields an expression reads.
 *
 * Asked of the engine, which is what evaluates the expression. This used to
 * walk a tree built by the TypeScript parser, and that tree was WRONG for a
 * lambda: `map(items, x => x + offset)` came back as `["items", "x"]` - the
 * body dropped, so `offset` was invisible, and `x`, a lambda parameter, was
 * reported as a field. The engine answers `["items", "offset"]`.
 *
 * What that cost: dependencies decide when a computed field or a rule is
 * re-evaluated. A field derived through a lambda would not re-run when the
 * value it reads changed - a stale number on screen, with nothing to see - and
 * the form would watch a field that does not exist.
 *
 * @param expr - Expression source (e.g., "age >= 18 && disclaimer_accepted")
 * @returns Field names referenced in the expression
 *
 * @example
 * extractExpressionDependencies("root.age >= 18") // ["age"]
 * extractExpressionDependencies("age >= 18 && active") // ["age", "active"]
 * extractExpressionDependencies("map(items, x => x + offset)") // ["items", "offset"]
 */
export function extractExpressionDependencies(expr: string): string[] {
  try {
    return engineDependencies(expr);
  } catch {
    // An expression that does not parse has no dependencies to report, which
    // is what the tree-walking version answered for the same case. The error
    // belongs to whoever compiles it, not to whoever asks what it reads.
    return [];
  }
}

/**
 * Extract field dependencies from a DSL instruction
 *
 * @param instruction - Parsed DSL instruction
 * @returns Array of field names referenced
 *
 * @example
 * extractInstructionDependencies({ kind: 'cmp', op: 'gte', field: 'age', other: '18' })
 * // ["age"]
 *
 * extractInstructionDependencies({ kind: 'within', field: 'admission', minField: 'start', maxField: 'end' })
 * // ["admission", "start", "end"]
 *
 * extractInstructionDependencies({ kind: 'refine', field: 'score', expr: 'value >= 0 && value <= 100' })
 * // ["score"] (expression deps handled separately if needed)
 */
export function extractInstructionDependencies(
  instruction: DslInstruction,
): string[] {
  const deps = new Set<string>();

  switch (instruction.kind) {
    case 'cmp': {
      deps.add(instruction.field);
      const treatStringAsField =
        instruction.op !== 'eq' && instruction.op !== 'neq';
      if (
        isFieldReference(
          instruction.other,
          instruction.otherType,
          treatStringAsField,
        )
      ) {
        deps.add(instruction.other);
      }
      break;
    }

    case 'within': {
      deps.add(instruction.field);
      if (
        isFieldReference(instruction.minField, instruction.minFieldType, true)
      ) {
        deps.add(instruction.minField);
      }
      if (
        isFieldReference(instruction.maxField, instruction.maxFieldType, true)
      ) {
        deps.add(instruction.maxField);
      }
      break;
    }

    case 'withinHours':
    case 'withinMinutes': {
      deps.add(instruction.field);
      if (isFieldReference(instruction.other, instruction.otherType, true)) {
        deps.add(instruction.other);
      }
      break;
    }

    case 'requiredIf': {
      deps.add(instruction.field);
      if (isFieldReference(instruction.other, instruction.otherType, true)) {
        deps.add(instruction.other);
      }
      break;
    }

    case 'requiredIfValue': {
      deps.add(instruction.field);
      if (isFieldReference(instruction.other, instruction.otherType, true)) {
        deps.add(instruction.other);
      }
      break;
    }

    case 'refine': {
      deps.add(instruction.field);
      break;
    }

    case 'notFutureDate': {
      deps.add(instruction.field);
      break;
    }

    case 'objectRefine': {
      const exprDeps = extractExpressionDependencies(instruction.expr);
      exprDeps.forEach((d) => deps.add(d));
      break;
    }

    case 'datesNotFuture': {
      instruction.fields.forEach((field) => {
        if (isFieldReference(field, undefined, true)) {
          deps.add(field);
        }
      });
      break;
    }
  }

  return Array.from(deps);
}

/**
 * Extract dependencies from multiple DSL instructions
 *
 * @param instructions - Array of parsed DSL instructions
 * @returns Unique array of all field names referenced across all instructions
 */
export function extractAllInstructionDependencies(
  instructions: DslInstruction[],
): string[] {
  const allDeps = new Set<string>();

  for (const instruction of instructions) {
    const deps = extractInstructionDependencies(instruction);
    deps.forEach((d) => allDeps.add(d));
  }

  return Array.from(allDeps);
}

/**
 * Check if a string looks like a literal value rather than a field name
 * Heuristic: starts with digit, quotes, or is a boolean/null keyword
 */
function isFieldReference(
  value: string,
  tokenType?: DslArgumentTokenType,
  treatStringAsField = false,
): boolean {
  if (!value) {
    return false;
  }

  if (tokenType === 'number') {
    return false;
  }

  if (tokenType === 'string') {
    if (!treatStringAsField) {
      return false;
    }
    return !isLiteralHeuristic(value);
  }

  if (tokenType === 'identifier') {
    const normalized = value.toLowerCase();
    if (
      normalized === 'true' ||
      normalized === 'false' ||
      normalized === 'null'
    ) {
      return false;
    }
    return true;
  }

  if (treatStringAsField) {
    return !isLiteralHeuristic(value);
  }
  return false;
}

function isLiteralHeuristic(str: string): boolean {
  if (!str) return true;

  if (/^-?\d/.test(str)) return true;
  if (str === 'true' || str === 'false' || str === 'null') return true;

  return false;
}
